import type { ChangeSet, PostureResult, ScenarioAnalysis } from '@prm/shared';
import { POSTURE_LABELS } from '@prm/shared';
import { mult, pct } from '../format';

const DECISION_COPY = {
  reduce: { label: 'Reduce risk', tone: 'bad' },
  hold: { label: 'Hold', tone: 'warn' },
  add: { label: 'Add exposure', tone: 'good' },
} as const;

/**
 * The verdict and the three numbers it rests on, across the top of the page.
 *
 * The reason line names what is binding rather than restating the verdict — the
 * point of the card is knowing what to fix, not what to feel.
 *
 * The prototype put a fourth "time to fix" card here. It is dropped rather than
 * filled: liquidating a book takes as long as its thinnest line allows, and we
 * hold no volume data, so any number in that slot would be invented.
 */
export function DecisionStrip({
  scenario,
  posture,
  changed,
}: {
  scenario: ScenarioAnalysis;
  posture: PostureResult;
  changed: ChangeSet;
}) {
  const { decision, regime, stress, policy } = scenario;
  const copy = DECISION_COPY[decision.decision];

  const worst = stress.worst;
  const worstLoss = worst === null ? null : Math.abs(worst.pctOfEquity);
  const volTarget = regime.volTargetDaily;
  const measuredVol = regime.realizedVol.daily;
  const volMultiple = measuredVol === null ? null : measuredVol / volTarget;

  return (
    <section className="decision-strip" aria-label="Portfolio decision" data-testid="decision-card">
      <article className={`decision-card decision-card--wide is-${copy.tone}`}>
        <span className="metric-label">Decision</span>
        <strong data-testid="decision-word">{copy.label}</strong>
        <small>{decision.reason}</small>

        <div className="decision-card__meta">
          <span className={`minichip ${policy.breachCount > 0 ? 'minichip--breach' : 'minichip--ok'}`}>
            {policy.breachCount} breach{policy.breachCount === 1 ? '' : 'es'}
          </span>
          <span className={`minichip ${scenario.playbook.cleared ? 'minichip--ok' : 'minichip--warn'}`}>
            playbook {scenario.playbook.cleared ? 'cleared' : 'blocked'}
          </span>
          <span className="minichip">
            posture {POSTURE_LABELS[posture.posture]} {posture.composite >= 0 ? '+' : ''}
            {posture.composite}/4
          </span>
        </div>

        {/* What moved since the last recorded session. Says so plainly when
            nothing did, rather than manufacturing a change out of drift. */}
        <div className={`decision-card__changed ${changed.material.length > 0 ? 'is-live' : ''}`}>
          <span className="metric-label">Changed</span>
          {changed.sentence}
        </div>
      </article>

      <BandCard
        label="Beta-adj net"
        value={regime.betaAdjNet}
        band={regime.band}
        breached={regime.bandBreach !== 0}
        advisory={regime.postureBandBinding ? null : regime.postureBand}
      />

      <LimitCard
        label="Worst stress"
        value={pct(worst?.pctOfEquity)}
        limit={`policy max ${pct(stress.maxDrawdown, 0)}`}
        breached={worstLoss !== null && worstLoss > stress.maxDrawdown}
        fill={worstLoss === null || stress.maxDrawdown === 0 ? 0 : worstLoss / stress.maxDrawdown}
        note={worst?.label ?? 'no scenario evaluated'}
      />

      <LimitCard
        label="Realized vol"
        value={pct(measuredVol, 2)}
        limit={`target ${pct(volTarget, 2)} daily`}
        breached={volMultiple !== null && volMultiple > 1}
        fill={volMultiple ?? 0}
        note={
          volMultiple === null
            ? `target ${pct(volTarget, 2)} daily`
            : `${volMultiple.toFixed(2)}× target · ${regime.realizedVol.window ?? '—'}d window`
        }
      />
    </section>
  );
}

/**
 * A value inside a two-sided band. Scaled so both edges are always on the track,
 * because "under the band" is as actionable as "over" it.
 */
function BandCard({
  label,
  value,
  band,
  breached,
  advisory,
}: {
  label: string;
  value: number;
  band: { min: number; max: number };
  breached: boolean;
  advisory: { min: number; max: number } | null;
}) {
  const scale = Math.max(band.max, value, advisory?.max ?? 0, 0.01) * 1.15;
  const at = (v: number) => `${Math.min(100, Math.max(0, (v / scale) * 100))}%`;

  return (
    <article className={`decision-card ${breached ? 'is-bad' : 'is-good'}`}>
      <span className="metric-label">{label}</span>
      <strong>{mult(value)}</strong>

      <div className="metric-bar">
        <div className="metric-bar__band" style={{ left: at(band.min), width: at(band.max - band.min) }} />
        {/* The advisory band is drawn but never measured against — the toggle
            that makes it binding lives in settings, not in this card. */}
        {advisory ? (
          <div
            className="metric-bar__advisory"
            style={{ left: at(advisory.min), width: at(advisory.max - advisory.min) }}
          />
        ) : null}
        <div className={`metric-bar__mark ${breached ? 'is-bad' : ''}`} style={{ left: at(value) }} />
      </div>

      <small>
        band {band.min.toFixed(2)}–{band.max.toFixed(2)}×
        {advisory ? (
          <>
            {' · '}
            <span className="advisory-key" /> posture {advisory.min.toFixed(2)}–{advisory.max.toFixed(2)}× advisory
          </>
        ) : null}
      </small>
    </article>
  );
}

/** A value against a single limit. Full width is always "at the line". */
function LimitCard({
  label,
  value,
  limit,
  breached,
  fill,
  note,
}: {
  label: string;
  value: string;
  limit: string;
  breached: boolean;
  fill: number;
  note?: string;
}) {
  return (
    <article className={`decision-card ${breached ? 'is-bad' : 'is-good'}`} title={limit}>
      <span className="metric-label">{label}</span>
      <strong>{value}</strong>
      <div className="metric-bar">
        <div
          className={`metric-bar__fill ${breached ? 'is-bad' : ''}`}
          style={{ width: `${Math.min(100, Math.max(0, fill * 100))}%` }}
        />
      </div>
      <small>{note ?? limit}</small>
    </article>
  );
}
